import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

const BASE_URL = "https://api.shubhamraj01.in";

const CUISINES = ["All", "Indian", "Chinese", "Italian", "Mughlai", "South Indian", "Fast Food"];

export default function RestaurantsPage({ setActivePage, setSelectedRestaurant }) {
  const { token } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [filtered, setFiltered]       = useState([]);
  const [search, setSearch]           = useState("");
  const [activeCuisine, setActiveCuisine] = useState("All");
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState("");

  useEffect(() => { loadRestaurants(); }, []);

  const loadRestaurants = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/restaurants`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Restaurants load nahi hue");
      const data = await res.json();
      setRestaurants(data);
      setFiltered(data);
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let result = [...restaurants];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(r =>
        r.name?.toLowerCase().includes(q) ||
        r.cuisineType?.toLowerCase().includes(q)
      );
    }
    if (activeCuisine !== "All") {
      result = result.filter(r =>
        r.cuisineType?.toLowerCase().includes(activeCuisine.toLowerCase())
      );
    }
    setFiltered(result);
  }, [search, activeCuisine, restaurants]);

  const handleRestaurantClick = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setActivePage("detail");
  };

  // Skeleton loader
  const Skeleton = () => (
    <div style={{
      background: "var(--surface)", border: "1.5px solid var(--border)",
      borderRadius: "20px", overflow: "hidden",
    }}>
      <div style={{
        height: "180px",
        background: "linear-gradient(90deg,var(--surface2) 25%,var(--border) 50%,var(--surface2) 75%)",
        backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite",
      }} />
      <div style={{ padding: "1rem" }}>
        <div style={{ height: "16px", width: "70%", marginBottom: ".6rem", borderRadius: "8px",
          background: "linear-gradient(90deg,var(--surface2) 25%,var(--border) 50%,var(--surface2) 75%)",
          backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite",
        }} />
        <div style={{ height: "12px", width: "45%", borderRadius: "8px",
          background: "linear-gradient(90deg,var(--surface2) 25%,var(--border) 50%,var(--surface2) 75%)",
          backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite",
        }} />
      </div>
    </div>
  );

  return (
    <div>
      {/* Page Header */}
      <div style={{
        padding: "2rem clamp(1rem,5vw,3rem)",
        borderBottom: "1px solid var(--border)",
        background: "var(--surface)",
      }}>
        <h2 style={{
          fontFamily: "'Syne', sans-serif", fontWeight: 800,
          fontSize: "1.7rem", letterSpacing: "-0.03em",
        }}>Restaurants</h2>
        <p style={{ color: "var(--text2)", fontSize: "0.9rem", marginTop: ".25rem" }}>
          Discover the best places to eat near you
        </p>

        {/* Search + Filter */}
        <div style={{ display: "flex", alignItems: "center", gap: ".75rem", marginTop: "1.25rem", flexWrap: "wrap" }}>
          {/* Search box */}
          <div style={{
            display: "flex", alignItems: "center", gap: ".5rem",
            background: "var(--bg)", border: "1.5px solid var(--border)",
            borderRadius: "12px", padding: ".5rem 1rem", flex: "1", minWidth: "200px",
          }}>
            <span>🔍</span>
            <input
              type="text" value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search restaurants or cuisines..."
              style={{
                border: "none", background: "transparent", outline: "none",
                fontSize: ".875rem", color: "var(--text)", width: "100%",
                fontFamily: "inherit",
              }}
            />
          </div>

          {/* Cuisine chips */}
          <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
            {CUISINES.map(c => (
              <button
                key={c}
                onClick={() => setActiveCuisine(c)}
                style={{
                  padding: ".35rem .85rem", borderRadius: "99px",
                  border: "1.5px solid",
                  borderColor: activeCuisine === c ? "var(--primary)" : "var(--border)",
                  background: activeCuisine === c ? "var(--primary)" : "var(--surface)",
                  color: activeCuisine === c ? "#fff" : "var(--text2)",
                  fontSize: ".78rem", fontWeight: 600, cursor: "pointer",
                  transition: "all .2s",
                }}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      <div style={{ padding: "2rem clamp(1rem,5vw,3rem)" }}>
        {error && (
          <div style={{ textAlign: "center", padding: "3rem", color: "var(--text3)" }}>
            <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>⚠️</div>
            <p>{error}</p>
          </div>
        )}

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: "1.25rem",
        }}>
          {loading ? (
            Array(6).fill(0).map((_, i) => <Skeleton key={i} />)
          ) : filtered.length === 0 ? (
            <div style={{
              gridColumn: "1/-1", textAlign: "center",
              padding: "4rem", color: "var(--text3)",
            }}>
              <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🍽️</div>
              <h3 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800 }}>No restaurants found</h3>
              <p style={{ marginTop: ".5rem", fontSize: ".9rem" }}>Try a different search or filter</p>
            </div>
          ) : (
            filtered.map(rest => (
              <div
                key={rest.id}
                onClick={() => handleRestaurantClick(rest)}
                style={{
                  background: "var(--surface)", border: "1.5px solid var(--border)",
                  borderRadius: "20px", overflow: "hidden", cursor: "pointer",
                  transition: "all .3s",
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow = "var(--shadow-lg)";
                  e.currentTarget.style.borderColor = "transparent";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.borderColor = "var(--border)";
                }}
              >
                {/* Image */}
                <div style={{ height: "180px", overflow: "hidden", background: "var(--surface2)", position: "relative" }}>
                  {rest.images?.[0] ? (
                    <img
                      src={rest.images[0]} alt={rest.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform .5s" }}
                    />
                  ) : (
                    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "52px" }}>
                      🍽️
                    </div>
                  )}
                  {/* Open/Closed badge */}
                  <div style={{
                    position: "absolute", top: "12px", right: "12px",
                    background: rest.open ? "rgba(45,106,79,.9)" : "rgba(153,27,27,.9)",
                    color: "#fff", fontSize: ".7rem", fontWeight: 700,
                    padding: ".25rem .6rem", borderRadius: "99px",
                  }}>
                    {rest.open ? "● OPEN" : "● CLOSED"}
                  </div>
                </div>

                {/* Info */}
                <div style={{ padding: "1rem 1.25rem 1.25rem" }}>
                  <h3 style={{
                    fontFamily: "'Syne', sans-serif", fontWeight: 700,
                    fontSize: "1rem", marginBottom: ".3rem",
                  }}>{rest.name}</h3>
                  <p style={{ fontSize: ".8rem", color: "var(--text3)", marginBottom: ".6rem" }}>
                    {rest.cuisineType} · {rest.openingHours}
                  </p>
                  <p style={{
                    fontSize: ".83rem", color: "var(--text2)",
                    lineHeight: 1.5, display: "-webkit-box",
                    WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                  }}>
                    {rest.description}
                  </p>
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    marginTop: ".75rem",
                  }}>
                    <span style={{ fontSize: ".78rem", color: "var(--text3)" }}>
                      📍 {rest.address?.city || ""}
                    </span>
                    <span style={{
                      fontSize: ".75rem", fontWeight: 600, color: "var(--primary)",
                      background: "var(--primary-l)", padding: ".2rem .6rem", borderRadius: "99px",
                    }}>
                      View Menu →
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}